import { useEffect, useMemo, useState } from "react";
import type { EvoLinkCategory, ExportScope, FilmProject, PackageIntent, PackageSourceMapSnapshot, ProductionPackageHistoryItem, ReviewGateState, UploadedAssetRecord } from "../types";
import type { MissingInput } from "../utils/analysisSummary";
import { agentPlanSummary, formatPlanRuntime, goalLabel, missingInputRouteLabel, missingInputToPackageRequired, reviewMissingInputsForScenes } from "../utils/analysisSummary";
import { buildPackageSourceMapSnapshot, diffPackageSourceMaps, packageSourceMapFromPackage } from "../utils/packageSourceMap";
import { createPackageDraft, evoLinkCategories, formatRuntime, scenesForScope } from "../utils/storyboard";
import { Icon } from "./Icon";
import { PackageSourceMap } from "./PackageSourceMap";

interface ReviewEvoLinkPanelProps {
  project: FilmProject;
  selectedSceneId: string;
  scope: ExportScope;
  categories: EvoLinkCategory[];
  gateState: ReviewGateState;
  onScopeChange: (scope: ExportScope) => void;
  onCategoriesChange: (categories: EvoLinkCategory[]) => void;
  onGateStateChange: (gateState: ReviewGateState) => void;
  onResolveMissingInput: (item: MissingInput) => void;
  onSavePackage: (intent?: PackageIntent) => void;
  onRenamePackage: (packageId: string, title: string) => void;
  onDuplicatePackage: (packageId: string) => void;
  onDeletePackage: (packageId: string) => void;
  isSavingPackage: boolean;
  onClose: () => void;
}

const categoryOrder: EvoLinkCategory[] = [
  "boards",
  "characters",
  "voices",
  "lines",
  "locations",
  "music",
  "sfx",
  "style",
  "colorGrade",
  "aspect",
  "chunkTiming"
];

const reviewGateItems: Array<{ id: keyof ReviewGateState; label: string; detail: string }> = [
  {
    id: "scriptRights",
    label: "Script rights confirmed",
    detail: "The uploaded script, notes, and shot list are authorized for this test package."
  },
  {
    id: "likenessRights",
    label: "Character likenesses cleared",
    detail: "Uploaded cards, generated characters, and face references can be used for pre-visuals."
  },
  {
    id: "voiceRights",
    label: "Voice/audio rights cleared",
    detail: "Actor recordings, generated voices, and narration choices are approved for the package."
  },
  {
    id: "brandIpRights",
    label: "Brand/IP safe for test",
    detail: "Logos, franchise elements, location marks, and third-party references have been reviewed."
  },
  {
    id: "humanReview",
    label: "Human review complete",
    detail: "A director/producer reviewed the generated boards, scope, and pass-through categories."
  },
  {
    id: "finalConfirm",
    label: "Final dry-run confirmation",
    detail: "I understand this prepares a dry-run package only; live EvoLink send remains off."
  }
];

const packageCategoryLabels: Record<string, string> = {
  storyboard: "Boards",
  boards: "Boards",
  characters: "Characters",
  voices: "Voices",
  lines: "Lines",
  sceneReferences: "Locations",
  locations: "Locations",
  music: "Music",
  sfx: "SFX",
  style: "Cinematic style",
  colorGrade: "Color grade",
  aspect: "Aspect ratio",
  chunkTiming: "Chunk timing"
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function recordValue(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function stringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function numberValue(value: unknown, fallback = 0): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function stringArrayValue(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => stringValue(item)).filter(Boolean) : [];
}

function recordArrayValue(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function packageTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "Saved package";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function packageVersionLabel(item: ProductionPackageHistoryItem, index: number, total: number): string {
  if (item.versionLabel) return item.versionLabel;
  return `v${Math.max(1, total - index)}`;
}

function packagePrepItems(item: ProductionPackageHistoryItem) {
  return [
    {
      label: "Dry-run safety",
      ok: item.dryRunOnly,
      detail: item.dryRunOnly ? "Live provider spend is still off." : "This package is not marked dry-run only."
    },
    {
      label: "Scene scope",
      ok: item.sceneIds.length > 0,
      detail: item.sceneIds.length ? `${item.sceneIds.length} scene${item.sceneIds.length === 1 ? "" : "s"} selected.` : "No scene IDs captured."
    },
    {
      label: "Storyboard frames",
      ok: item.frameCount > 0,
      detail: item.frameCount ? `${item.frameCount} frame${item.frameCount === 1 ? "" : "s"} included.` : "No boards included."
    },
    {
      label: "Runtime chunks",
      ok: item.runtimeSeconds > 0 && item.chunkCount > 0,
      detail: `${formatRuntime(item.runtimeSeconds)} · ${item.chunkCount} chunk${item.chunkCount === 1 ? "" : "s"}.`
    },
    {
      label: "Required inputs",
      ok: item.missingCount === 0,
      detail: item.missingCount ? `${item.missingCount} missing input${item.missingCount === 1 ? "" : "s"}.` : "No required inputs missing."
    },
    {
      label: "Review blockers",
      ok: item.blocked.length === 0,
      detail: item.blocked.length ? `${item.blocked.length} blocker${item.blocked.length === 1 ? "" : "s"} still open.` : "No blockers recorded."
    }
  ];
}

function packageSummaryText(item: ProductionPackageHistoryItem): string {
  const metadata = packageMetadata(item);
  return [
    `Kinetix Film EvoLink dry-run package`,
    `Title: ${item.title}`,
    `Version: ${item.versionLabel || "legacy"}`,
    `Source: ${metadata.sourceLabel}`,
    `Saved: ${packageTimestamp(item.createdAt)}`,
    `Scope: ${item.scopeLabel} (${item.sceneIds.length} scene${item.sceneIds.length === 1 ? "" : "s"})`,
    `Output: ${item.output}`,
    `Runtime: ${formatRuntime(item.runtimeSeconds)} · ${item.chunkCount} chunk${item.chunkCount === 1 ? "" : "s"}`,
    `Assets: ${item.frameCount} frame${item.frameCount === 1 ? "" : "s"}, ${item.characterCount} character reference${item.characterCount === 1 ? "" : "s"}, ${item.voiceCount} voice assignment${item.voiceCount === 1 ? "" : "s"}`,
    `Included: ${metadata.includedCategories.join(", ") || "none"}`,
    `Excluded: ${metadata.excludedCategories.join(", ") || "none"}`,
    metadata.agentSummary ? `Agent strategy: ${metadata.agentSummary}` : "",
    `Status: ${item.ready ? "ready" : item.status}`,
    `Dry run only: ${item.dryRunOnly ? "yes" : "no"}`,
    item.blocked.length ? `Blocked: ${item.blocked.join("; ")}` : "Blocked: none"
  ].filter(Boolean).join("\n");
}

function categoryNamesFromRecord(categories: Record<string, unknown>, expected: boolean): string[] {
  return Object.entries(categories)
    .filter(([, value]) => Boolean(value) === expected)
    .map(([key]) => packageCategoryLabels[key] || key)
    .filter((label, index, labels) => labels.indexOf(label) === index);
}

function packageMetadata(item: ProductionPackageHistoryItem) {
  const sourcePackage = item.package || {};
  const schema = stringValue(sourcePackage.schema);
  const providerPayload = recordValue(sourcePackage.providerPayload);
  const providerDryRun = recordValue(sourcePackage.providerDryRun);
  const categories = recordValue(sourcePackage.categories);
  const agentStrategy = recordValue(sourcePackage.agentStrategy);
  const confidence = numberValue(agentStrategy.confidence);
  const providerVersion = stringValue(providerPayload.version || providerDryRun.version, "dry-run.v1");
  const includedCategories = categoryNamesFromRecord(categories, true);
  const excludedCategories = categoryNamesFromRecord(categories, false);
  return {
    sourceLabel: schema.includes("local_fallback") ? "Local fallback" : "Backend package",
    providerVersion,
    includedCategories,
    excludedCategories,
    agentSummary: stringValue(agentStrategy.summary),
    agentGoal: stringValue(agentStrategy.goal),
    agentConfidence: confidence ? `${Math.round(confidence * 100)}%` : stringValue(agentStrategy.confidenceLabel),
    recommendedSceneIds: stringArrayValue(agentStrategy.recommendedSceneIds),
    providerReady: providerPayload.ready === true,
    dryRunStatus: stringValue(providerDryRun.status || sourcePackage.status)
  };
}

function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied ? Promise.resolve() : Promise.reject(new Error("Clipboard permission denied."));
}

function safeFileName(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 72) || "kinetix-package";
}

function packageForExport(item: ProductionPackageHistoryItem): Record<string, unknown> {
  const sourceMap = item.sourceMap || packageSourceMapFromPackage(item.package);
  if (!sourceMap) return item.package;
  const rest = { ...item.package };
  delete rest.sourceMap;
  return { sourceMap, ...rest };
}

function downloadPackageJson(item: ProductionPackageHistoryItem) {
  const blob = new Blob([JSON.stringify(packageForExport(item), null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${safeFileName(item.title)}-${item.id}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function packageJsonPreview(item: ProductionPackageHistoryItem): string {
  const text = JSON.stringify(packageForExport(item), null, 2);
  const limit = 100000;
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}\n\n... Package JSON preview truncated at ${limit.toLocaleString()} characters. Use Download JSON for the complete package.`;
}

function sourceMapSummaryText(sourceMap?: PackageSourceMapSnapshot): string {
  if (!sourceMap) return "No frozen source map";
  return [
    `${sourceMap.totals.includedBoardCount} boards`,
    `${sourceMap.totals.voiceAssignmentCount} voices`,
    `${sourceMap.totals.referenceAssignmentCount} refs`,
    `${sourceMap.totals.chunkCount} chunks`
  ].join(" · ");
}

function packageDiffStateLabel(state: string): string {
  if (state === "no_baseline") return "No baseline";
  if (state === "blocked") return "Blocked";
  if (state === "changed") return "Changed";
  return "Unchanged";
}

function packageDiffStateClass(state: string): string {
  return state.replace(/_/g, "-");
}

function missingInputFocusLabel(item: MissingInput, project: FilmProject): string {
  const focusedCharacter = item.focusId
    ? project.characters.find((character) => character.id === item.focusId)
    : undefined;
  const focusedScene = item.sceneId
    ? project.scenes.find((scene) => scene.id === item.sceneId)
    : item.focusId
      ? project.scenes.find((scene) => scene.id === item.focusId)
      : undefined;
  if (focusedCharacter) return `${focusedCharacter.name} focused`;
  if (focusedScene) return `Sc ${focusedScene.number} focused`;
  if (item.focusId) return `${item.focusId} focused`;
  return "Focused repair";
}

export function ReviewEvoLinkPanel({
  project,
  selectedSceneId,
  scope,
  categories,
  gateState,
  onScopeChange,
  onCategoriesChange,
  onGateStateChange,
  onResolveMissingInput,
  onSavePackage,
  onRenamePackage,
  onDuplicatePackage,
  onDeletePackage,
  isSavingPackage,
  onClose
}: ReviewEvoLinkPanelProps) {
  const draft = createPackageDraft(project, selectedSceneId, scope, categories);
  const selectedScene = project.scenes.find((scene) => scene.id === selectedSceneId);
  const selectedScenes = scenesForScope(project, selectedSceneId, "selected");
  const beatScenes = scenesForScope(project, selectedSceneId, "beats");
  const assignedVoices = project.characters.filter((character) => character.voiceStatus === "assigned" || character.voiceStatus === "not_needed").length;
  const voiceSlots = project.characters.filter((character) => character.voiceStatus !== "not_needed").length;
  const readyCharacterCards = project.characters.filter((character) => character.cardStatus !== "missing").length;
  const agentSummary = useMemo(() => agentPlanSummary(project.apiPlan || {}, project), [project]);
  const agentScopeBeats = agentSummary.beats.slice(0, 3);
  const packages = project.productionPackages || [];
  const [selectedPackageId, setSelectedPackageId] = useState(packages[0]?.id || "");
  const [seenPackageCount, setSeenPackageCount] = useState(packages.length);
  const [historyMessage, setHistoryMessage] = useState("");
  const [historyDetailMode, setHistoryDetailMode] = useState<"compare" | "snapshot">("compare");
  const [renameTitle, setRenameTitle] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState("");
  const latestPackage = packages[0];
  const selectedPackage = packages.find((item) => item.id === selectedPackageId) || packages[0];
  const selectedPackageIndex = selectedPackage ? packages.findIndex((item) => item.id === selectedPackage.id) : -1;
  const latestPackageVersion = latestPackage ? packageVersionLabel(latestPackage, 0, packages.length) : "";
  const selectedPackageVersion = selectedPackage && selectedPackageIndex >= 0
    ? packageVersionLabel(selectedPackage, selectedPackageIndex, packages.length)
    : "";
  const prepItems = selectedPackage ? packagePrepItems(selectedPackage) : [];
  const prepReadyCount = prepItems.filter((item) => item.ok).length;
  const prepReady = prepItems.length > 0 && prepReadyCount === prepItems.length;
  const selectedPackageMetadata = selectedPackage ? packageMetadata(selectedPackage) : null;
  const selectedPackageSourceMap = selectedPackage
    ? selectedPackage.sourceMap || packageSourceMapFromPackage(selectedPackage.package)
    : undefined;
  const includedCategories = categoryOrder
    .filter((category) => categories.includes(category))
    .map((category) => evoLinkCategories.find((item) => item.id === category)?.label || category);
  const excludedCategories = categoryOrder
    .filter((category) => !categories.includes(category))
    .map((category) => evoLinkCategories.find((item) => item.id === category)?.label || category);
  const scopedReviewScenes = scenesForScope(project, selectedSceneId, scope);
  const missingInputs = reviewMissingInputsForScenes(project, scopedReviewScenes, { includeBoards: categories.includes("boards") });
  const missingRequired = missingInputs.map(missingInputToPackageRequired);
  const localOnlySourceCount = project.uploadedAssets
    ? (Object.values(project.uploadedAssets) as UploadedAssetRecord[][]).reduce(
        (total, bucket) => total + bucket.filter((record) => record.cloudStatus === "local_only" || record.localOnly).length,
        0
      )
    : 0;
  const uncheckedGates = reviewGateItems.filter((item) => !gateState[item.id]);
  const checkedGateCount = reviewGateItems.length - uncheckedGates.length;
  const blockedReasons = [
    ...missingInputs.map((item) => `Missing: ${item.label}`),
    ...uncheckedGates.map((item) => `Gate: ${item.label}`)
  ];
  const dryRunReady = blockedReasons.length === 0;
  const currentPackageSourceMap = buildPackageSourceMapSnapshot(project, selectedSceneId, scope, categories);
  const latestPackageSourceMap = latestPackage
    ? latestPackage.sourceMap || packageSourceMapFromPackage(latestPackage.package)
    : undefined;
  const packageDiff = diffPackageSourceMaps(currentPackageSourceMap, latestPackageSourceMap, {
    currentBlockers: blockedReasons,
    previousBlockers: latestPackage?.blocked || []
  });
  const selectedVsCurrentDiff = selectedPackageSourceMap
    ? diffPackageSourceMaps(currentPackageSourceMap, selectedPackageSourceMap, {
        currentBlockers: blockedReasons,
        previousBlockers: selectedPackage?.blocked || []
      })
    : undefined;
  const scopeOptions: Array<{ id: ExportScope; label: string; detail: string }> = [
    { id: "current", label: "Current scene", detail: selectedScene ? `Sc ${selectedScene.number} only` : "1 scene" },
    { id: "selected", label: "Selected scenes", detail: `${selectedScenes.length} picked` },
    { id: "beats", label: "Trailer beats", detail: `${project.agentBeats.length || 0} beats · ${beatScenes.length} scenes` },
    { id: "all", label: "Full script", detail: `All ${project.scenes.length} scenes` }
  ];
  const scopeTitle = scopeOptions.find((option) => option.id === scope)?.label || "Current scene";
  const currentPackageStatus = dryRunReady
    ? "Ready"
    : `${blockedReasons.length} blocker${blockedReasons.length === 1 ? "" : "s"}`;

  function toggleCategory(category: EvoLinkCategory) {
    if (categories.includes(category)) {
      onCategoriesChange(categories.filter((item) => item !== category));
    } else {
      onCategoriesChange([...categories, category]);
    }
  }

  function toggleGate(id: keyof ReviewGateState) {
    onGateStateChange({ ...gateState, [id]: !gateState[id] });
  }

  useEffect(() => {
    if (!packages.length) {
      setSelectedPackageId("");
      setSeenPackageCount(0);
      return;
    }
    if (packages.length > seenPackageCount) {
      setSelectedPackageId(packages[0].id);
      setSeenPackageCount(packages.length);
      return;
    }
    if (packages.length !== seenPackageCount) {
      setSeenPackageCount(packages.length);
    }
    if (!packages.some((item) => item.id === selectedPackageId)) {
      setSelectedPackageId(packages[0].id);
    }
  }, [packages, selectedPackageId, seenPackageCount]);

  useEffect(() => {
    setRenameTitle(selectedPackage?.title || "");
    setDeleteConfirmId("");
    setHistoryDetailMode("compare");
  }, [selectedPackage?.id, selectedPackage?.title]);

  async function handleCopyPackageSummary(item: ProductionPackageHistoryItem) {
    try {
      await copyText(packageSummaryText(item));
      setHistoryMessage("Package summary copied.");
    } catch (error) {
      setHistoryMessage(`Copy failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function handleRenameSelectedPackage() {
    if (!selectedPackage) return;
    onRenamePackage(selectedPackage.id, renameTitle);
    setHistoryMessage("Package name updated.");
  }

  function handleDeleteSelectedPackage() {
    if (!selectedPackage) return;
    if (deleteConfirmId !== selectedPackage.id) {
      setDeleteConfirmId(selectedPackage.id);
      setHistoryMessage("Click Confirm delete to remove this saved package.");
      return;
    }
    onDeletePackage(selectedPackage.id);
    setDeleteConfirmId("");
    setHistoryMessage("Package deleted.");
  }

  return (
    <section className="review-panel" aria-label="Review EvoLink package">
      <header>
        <div>
          <h2>Review EvoLink package</h2>
          <p>Backend-backed dry run assembled from your scenes, boards, voices, and look.</p>
        </div>
        <button className="icon-button" onClick={onClose} type="button" aria-label="Close Review EvoLink">
          <Icon name="chevron" />
        </button>
      </header>

      <div className="review-package-grid">
        <div className="review-package-main">
          <div className="dry-run-banner">
            <strong>DRY RUN - no provider credits spent yet.</strong>
            <span>Live send unlocks only after rights-safe + confirm.</span>
          </div>

          <div className="review-version-strip" aria-label="Current and saved package state">
            <article className="current">
              <span>Current Review Package</span>
              <strong>{scopeTitle} · {draft.sceneIds.length} scene{draft.sceneIds.length === 1 ? "" : "s"}</strong>
              <small>{draft.frameCount} boards · {formatRuntime(draft.runtimeSeconds)} · {draft.chunkCount} chunks · {currentPackageStatus}</small>
            </article>
            <article className={latestPackage ? "saved" : "empty"}>
              <span>Latest Saved Package</span>
              {latestPackage ? (
                <>
                  <strong>{latestPackage.versionLabel || latestPackageVersion} · {latestPackage.title}</strong>
                  <small>{packageTimestamp(latestPackage.createdAt)} · {sourceMapSummaryText(latestPackageSourceMap)}</small>
                </>
              ) : (
                <>
                  <strong>No saved version yet</strong>
                  <small>Save this dry-run package to create v1 and unlock comparisons.</small>
                </>
              )}
            </article>
          </div>

          <section className="review-agent-card" aria-label="Production agent strategy">
            <header>
              <div>
                <span className="agent-dot" />
                <strong>Kinetix Production Agent</strong>
              </div>
              <small>Strategy included in this dry-run package</small>
            </header>
            <p><b>{goalLabel(agentSummary.goal)} strategy:</b> {agentSummary.strategy}</p>
            <div className="review-agent-meta">
              <span>{agentSummary.confidence}% confidence</span>
              <span>{formatPlanRuntime(agentSummary)}</span>
              <span>{agentSummary.missingInputs.length} missing input{agentSummary.missingInputs.length === 1 ? "" : "s"}</span>
            </div>
            {agentScopeBeats.length ? (
              <div className="review-agent-beats" aria-label="Agent beat sample">
                {agentScopeBeats.map((beat) => (
                  <span
                    aria-label={`${beat.label}: ${beat.sceneLabel}, ${beat.seconds} seconds`}
                    className={beat.readiness === "ready" ? "ready" : "needs-input"}
                    key={beat.id}
                  >
                    <strong>{beat.label}</strong>
                    <small>{beat.sceneLabel} · {beat.seconds}s</small>
                  </span>
                ))}
              </div>
            ) : null}
            <small>The agent can assemble and explain the package, but it cannot spend credits, approve rights, or overwrite uploads.</small>
          </section>

          <div className="package-assembly-strip" aria-label="Package assembly summary">
            <div>
              <span>Current Review Package</span>
              <strong>Production package v1</strong>
              <small>Saved packages are built through `/api/production-package` when available.</small>
            </div>
            <div>
              <span>Included Lanes</span>
              <strong>{includedCategories.length} enabled</strong>
              <small>{includedCategories.slice(0, 5).join(" · ")}{includedCategories.length > 5 ? ` · +${includedCategories.length - 5}` : ""}</small>
            </div>
            <div>
              <span>Source Assignments</span>
              <strong>{draft.characterCount} cast · {draft.voiceCount} voice · {draft.referenceCount} ref</strong>
              <small>{draft.customFrameAssignmentCount ? `${draft.customFrameAssignmentCount} board override${draft.customFrameAssignmentCount === 1 ? "" : "s"}` : "Scene defaults"}</small>
            </div>
            <div>
              <span>Excluded</span>
              <strong>{excludedCategories.length}</strong>
              <small>{excludedCategories.length ? excludedCategories.join(" · ") : "Nothing excluded"}</small>
            </div>
          </div>

          <div className="control-group">
            <span>Scope</span>
            <div className="scope-card-grid">
              {scopeOptions.map((option) => (
                <button
                  className={scope === option.id ? "active" : ""}
                  key={option.id}
                  onClick={() => onScopeChange(option.id)}
                  type="button"
                >
                  <strong>{option.label}</strong>
                  <small>{option.detail}</small>
                </button>
              ))}
            </div>
          </div>

          <div className="control-group">
            <span>Pass-through</span>
            <div className="category-list compact">
              {categoryOrder.map((categoryId) => {
                const category = evoLinkCategories.find((item) => item.id === categoryId);
                if (!category) return null;
                const checked = categories.includes(category.id);
                return (
                  <label className={!checked ? "excluded" : ""} key={category.id}>
                    <input
                      checked={checked}
                      onChange={() => toggleCategory(category.id)}
                      type="checkbox"
                    />
                    <span>
                      <strong>{category.label}</strong>
                      <small>{checked ? category.description : "excluded"}</small>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <p className="review-footnote">Unchecked categories never enter the package or prompt. Single scenes or chunks can be disabled from their own rows.</p>

          <div className="review-chunk-list" aria-label="EvoLink chunk payload">
            <div>
              <span>Chunk Payload</span>
              <strong>{draft.chunkCount} included · {draft.disabledChunkCount} disabled</strong>
            </div>
            {draft.chunks.slice(0, 8).map((chunk) => (
              <article key={chunk.id}>
                <span>{String(chunk.index).padStart(2, "0")}</span>
                <div>
                  <strong>{chunk.label}</strong>
                  <small>{formatRuntime(chunk.durationSeconds)} · {chunk.frameIds.length} board{chunk.frameIds.length === 1 ? "" : "s"}{chunk.locked ? " · locked" : ""}</small>
                </div>
              </article>
            ))}
            {draft.chunks.length > 8 ? <small>{draft.chunks.length - 8} more chunk{draft.chunks.length - 8 === 1 ? "" : "s"} in package JSON.</small> : null}
          </div>

          <section className={`package-diff-card ${packageDiffStateClass(packageDiff.state)} ${packageDiff.changedCount ? "changed" : "clean"}`} aria-label="Changes since latest saved package">
            <header>
              <div>
                <div className="package-diff-title-row">
                  <span>Changes since Latest Saved Package</span>
                  <b className={`package-diff-state ${packageDiffStateClass(packageDiff.state)}`}>
                    {packageDiffStateLabel(packageDiff.state)}
                  </b>
                </div>
                <strong>{packageDiff.summary}</strong>
              </div>
              <small>
                {packageDiff.baselineAvailable && latestPackage
                  ? `Compared with ${latestPackage.versionLabel || latestPackageVersion || "latest"} · ${packageTimestamp(latestPackage.createdAt)}`
                  : "No saved baseline yet"}
              </small>
            </header>
            <div className="package-diff-list">
              {packageDiff.rows.map((row) => (
                <article className={`${row.changed ? "changed" : "clean"} ${row.tone}`} key={row.id}>
                  <span aria-hidden="true" />
                  <div>
                    <strong>{row.label}</strong>
                    <small>{row.detail}</small>
                  </div>
                  <b>{row.value}</b>
                  <em>{row.delta}</em>
                </article>
              ))}
            </div>
            <details className="advanced-drawer compact-drawer">
              <summary>Diff JSON</summary>
              <pre>{JSON.stringify(packageDiff.raw, null, 2).slice(0, 50000)}</pre>
            </details>
          </section>

          <PackageSourceMap
            project={project}
            selectedSceneId={selectedSceneId}
            scope={scope}
            categories={categories}
            title="Current Review Package Source Map"
            defaultCollapsed
            subtitle={`${currentPackageSourceMap.totals.customBoardOverrideCount} overrides · ${currentPackageSourceMap.totals.referenceAssignmentCount} references · ${currentPackageSourceMap.totals.chunkCount} chunks`}
          />

          <details className="advanced-drawer">
            <summary>Advanced</summary>
            <pre>{JSON.stringify({
              scope: { type: draft.scope, sceneIds: draft.sceneIds },
              dryRun: true,
              categories: Object.fromEntries(evoLinkCategories.map((category) => [category.id, categories.includes(category.id)])),
              frames: categories.includes("boards") ? draft.frameCount : 0,
              sourceAssignments: {
                characters: categories.includes("characters") ? draft.characterCount : 0,
                voices: categories.includes("voices") ? draft.voiceCount : 0,
                references: categories.includes("locations") ? draft.referenceCount : 0,
                customBoardOverrides: draft.customFrameAssignmentCount
              },
              chunks: categories.includes("chunkTiming") ? draft.chunks : [],
              reviewGate: gateState,
              packageIntentOptions: ["save", "send_test"],
              liveSendEnabled: false,
              providerSpendAllowed: false,
              providerPayloadReady: dryRunReady,
              missingRequired,
            }, null, 2)}</pre>
          </details>

          <div className="package-history" aria-label="Saved EvoLink dry-run packages">
            <header>
              <div>
                <span>Package History Versions</span>
                <strong>{packages.length ? `Latest Saved Package · ${latestPackageVersion || "v1"}` : "No packages yet"}</strong>
              </div>
              {historyMessage ? <small role="status">{historyMessage}</small> : null}
            </header>

            {packages.length ? (
              <div className="package-history-grid">
                <div className="package-history-list">
                  {packages.slice(0, 8).map((item, index) => (
                    <button
                      className={selectedPackage?.id === item.id ? "active" : ""}
                      key={item.id}
                      onClick={() => {
                        setSelectedPackageId(item.id);
                        setHistoryMessage("");
                      }}
                      type="button"
                    >
                      <span className="package-history-badges">
                        <span className={item.ready ? "ready" : "blocked"}>{item.ready ? "Ready" : "Blocked"}</span>
                        <span className="version">{packageVersionLabel(item, index, packages.length)}</span>
                        {index === 0 ? <span className="latest">Latest</span> : null}
                      </span>
                      <strong>{item.title}</strong>
                      <small>{packageTimestamp(item.createdAt)} · {formatRuntime(item.runtimeSeconds)} · {item.chunkCount} chunks</small>
                    </button>
                  ))}
                </div>

                {selectedPackage ? (
                  <article className="package-detail-card">
                    <div>
                      <span>
                        {selectedPackageIndex === 0 ? "Latest Saved Package" : "Saved Package Snapshot"}
                        {selectedPackageVersion ? ` · ${selectedPackageVersion}` : ""}
                        {selectedPackage.packageIntent === "send_test" ? " · 480p test" : " · dry run"}
                      </span>
                      <strong>{selectedPackage.title}</strong>
                      <small>{packageTimestamp(selectedPackage.createdAt)} · {selectedPackage.output}</small>
                    </div>
                    <div className="package-title-editor">
                      <label>
                        <span>Package name</span>
                        <input
                          value={renameTitle}
                          onChange={(event) => setRenameTitle(event.target.value)}
                          type="text"
                        />
                      </label>
                      <button
                        className="secondary-action"
                        disabled={!renameTitle.trim() || renameTitle.trim() === selectedPackage.title}
                        onClick={handleRenameSelectedPackage}
                        type="button"
                      >
                        Rename
                      </button>
                    </div>
                    {selectedPackageMetadata ? (
                      <section className="package-source-card" aria-label="Package source and agent strategy">
                        <div>
                          <span>Source</span>
                          <strong>{selectedPackageMetadata.sourceLabel}</strong>
                          <small>{selectedPackageMetadata.providerVersion} · {selectedPackageMetadata.dryRunStatus}</small>
                        </div>
                        <div>
                          <span>Agent Strategy</span>
                          <strong>{selectedPackageMetadata.agentGoal || project.goal}</strong>
                          <small>
                            {selectedPackageMetadata.agentSummary || "Scene/chunk package assembled from current project state."}
                            {selectedPackageMetadata.agentConfidence ? ` · confidence ${selectedPackageMetadata.agentConfidence}` : ""}
                          </small>
                        </div>
                      </section>
                    ) : null}
                    <div className="package-view-toggle" aria-label="Saved package view mode">
                      <button
                        className={historyDetailMode === "compare" ? "active" : ""}
                        onClick={() => setHistoryDetailMode("compare")}
                        type="button"
                      >
                        Compare to current
                      </button>
                      <button
                        className={historyDetailMode === "snapshot" ? "active" : ""}
                        onClick={() => setHistoryDetailMode("snapshot")}
                        type="button"
                      >
                        View snapshot
                      </button>
                    </div>
                    <dl>
                      <div><dt>Scenes</dt><dd>{selectedPackage.sceneIds.length}</dd></div>
                      <div><dt>Frames</dt><dd>{selectedPackage.frameCount}</dd></div>
                      <div><dt>Voices</dt><dd>{selectedPackage.voiceCount}</dd></div>
                      <div><dt>Cards</dt><dd>{selectedPackage.characterCount}</dd></div>
                      <div><dt>Runtime</dt><dd>{formatRuntime(selectedPackage.runtimeSeconds)}</dd></div>
                      <div><dt>Chunks</dt><dd>{selectedPackage.chunkCount}</dd></div>
                    </dl>
                    {historyDetailMode === "compare" ? (
                      <section className={`package-insight-section package-current-compare ${selectedVsCurrentDiff?.changedCount ? "changed" : "clean"}`} aria-label="Compare current review package to saved snapshot">
                        <header>
                          <span>Compare Current Review Package</span>
                          <strong>
                            {selectedVsCurrentDiff
                              ? `${selectedVsCurrentDiff.changedCount} change${selectedVsCurrentDiff.changedCount === 1 ? "" : "s"} vs ${selectedPackageVersion || "saved"}`
                              : "Snapshot unavailable"}
                          </strong>
                        </header>
                        {selectedVsCurrentDiff ? (
                          <div className="package-diff-list compact">
                            {selectedVsCurrentDiff.rows.map((row) => (
                              <article className={`${row.changed ? "changed" : "clean"} ${row.tone}`} key={row.id}>
                                <span aria-hidden="true" />
                                <div>
                                  <strong>{row.label}</strong>
                                  <small>{row.detail}</small>
                                </div>
                                <b>{row.value}</b>
                                <em>{row.delta}</em>
                              </article>
                            ))}
                          </div>
                        ) : (
                          <p className="package-muted-note">This saved package predates frozen source-map snapshots, so it cannot be compared without recalculating history.</p>
                        )}
                      </section>
                    ) : null}

                    {selectedPackageMetadata ? (
                      <section className="package-insight-section" aria-label="Package pass-through categories">
                        <header>
                          <span>Pass-through</span>
                          <strong>{selectedPackageMetadata.includedCategories.length} included · {selectedPackageMetadata.excludedCategories.length} excluded</strong>
                        </header>
                        <div className="package-category-pills">
                          {selectedPackageMetadata.includedCategories.map((item) => (
                            <span className="included" key={`included-${item}`}>{item}</span>
                          ))}
                          {selectedPackageMetadata.excludedCategories.map((item) => (
                            <span className="excluded" key={`excluded-${item}`}>{item}</span>
                          ))}
                        </div>
                      </section>
                    ) : null}

                    {historyDetailMode === "snapshot" && selectedPackageSourceMap ? (
                      <PackageSourceMap
                        snapshot={selectedPackageSourceMap}
                        title="Saved Package Snapshot"
                        subtitle={`${packageTimestamp(selectedPackageSourceMap.createdAt)} · frozen source map`}
                        compact
                        defaultCollapsed
                      />
                    ) : historyDetailMode === "snapshot" ? (
                      <section className="package-insight-section" aria-label="Saved package snapshot unavailable">
                        <header>
                          <span>Saved Package Snapshot</span>
                          <strong>Not captured</strong>
                        </header>
                        <p className="package-muted-note">This legacy package was saved before source-map snapshots were added.</p>
                      </section>
                    ) : null}

                    <section className={`package-insight-section package-prep ${prepReady ? "ready" : "blocked"}`} aria-label="Package export readiness">
                      <header>
                        <span>Export Readiness</span>
                        <strong>{prepReadyCount} / {prepItems.length} ready</strong>
                      </header>
                      <div className="package-readiness-list">
                        {prepItems.map((item) => (
                          <div className={item.ok ? "ready" : "blocked"} key={item.label}>
                            <span aria-hidden="true" />
                            <strong>{item.label}</strong>
                            <small>{item.detail}</small>
                          </div>
                        ))}
                      </div>
                    </section>
                    {selectedPackage.blocked.length ? (
                      <div className="package-blockers">
                        <strong>Blocked</strong>
                        {selectedPackage.blocked.slice(0, 5).map((item) => <span key={item}>{item}</span>)}
                        {selectedPackage.blocked.length > 5 ? <small>{selectedPackage.blocked.length - 5} more blocker{selectedPackage.blocked.length - 5 === 1 ? "" : "s"}</small> : null}
                      </div>
                    ) : (
                      <p className="package-ready-note">Ready as a dry-run package. Live provider send is still disabled.</p>
                    )}
                    <div className="package-history-actions">
                      <button className="secondary-action" onClick={() => void handleCopyPackageSummary(selectedPackage)} type="button">
                        <Icon name="link" />
                        Copy summary
                      </button>
                      <button className="secondary-action" onClick={() => downloadPackageJson(selectedPackage)} type="button">
                        <Icon name="download" />
                        Download JSON
                      </button>
                      <button className="secondary-action" onClick={() => onDuplicatePackage(selectedPackage.id)} type="button">
                        <Icon name="plus" />
                        Duplicate as new package
                      </button>
                      <button className={`secondary-action ${deleteConfirmId === selectedPackage.id ? "danger" : ""}`} onClick={handleDeleteSelectedPackage} type="button">
                        {deleteConfirmId === selectedPackage.id ? "Confirm delete" : "Delete package"}
                      </button>
                    </div>
                    <details className="advanced-drawer compact-drawer">
                      <summary>Package JSON</summary>
                      <pre>{packageJsonPreview(selectedPackage)}</pre>
                    </details>
                  </article>
                ) : null}
              </div>
            ) : (
              <section className="package-history-empty">
                <Icon name="download" />
                <strong>No saved dry-run packages yet</strong>
                <p>Save the current review package to create v1. Kinetix freezes the source map and keeps live EvoLink spend off.</p>
                <div className="package-history-actions">
                  <button className="primary-action" disabled={isSavingPackage} onClick={() => onSavePackage("save")} type="button">
                    <Icon name="download" />
                    {isSavingPackage ? "Saving Package" : "Save package"}
                  </button>
                  <button
                    className="secondary-action"
                    disabled={isSavingPackage || !dryRunReady}
                    onClick={() => onSavePackage("send_test")}
                    title={dryRunReady ? "Prepare a dry-run 480p test package" : blockedReasons.slice(0, 3).join(" · ")}
                    type="button"
                  >
                    <Icon name="play" />
                    {dryRunReady ? "Prepare 480p test" : "Resolve gates first"}
                  </button>
                </div>
              </section>
            )}
          </div>
        </div>

        <aside className="package-summary-card">
          <span>Package Summary</span>
          <h3>{scopeTitle} · {draft.sceneIds.length} scene{draft.sceneIds.length === 1 ? "" : "s"}</h3>
          <dl>
            <div>
              <dt>Frames</dt>
              <dd>{draft.frameCount} · {draft.uploadedFrameCount} uploaded · {draft.lockedFrameCount} locked</dd>
            </div>
            <div>
              <dt>Voices</dt>
              <dd>{assignedVoices} of {voiceSlots || project.characters.length} assigned</dd>
            </div>
            <div>
              <dt>Character cards</dt>
              <dd className={readyCharacterCards < project.characters.length ? "warn" : ""}>{readyCharacterCards} of {project.characters.length}</dd>
            </div>
            <div>
              <dt>Runtime</dt>
              <dd>{formatRuntime(draft.runtimeSeconds)} · {draft.chunkCount} chunks{draft.disabledChunkCount ? ` · ${draft.disabledChunkCount} disabled` : ""}</dd>
            </div>
            <div>
              <dt>Output</dt>
              <dd>{draft.output}</dd>
            </div>
            <div>
              <dt>Est. provider cost</dt>
              <dd className="cost">{draft.dryRunCost}</dd>
            </div>
          </dl>

          {missingInputs.length ? (
            <div className="missing-items">
              <strong>Missing · {missingInputs.length}</strong>
              {missingInputs.map((item) => (
                <div className="missing-action-row" key={item.id}>
                  <span>
                    <strong>{item.label}</strong>
                    <small>Fix in {missingInputRouteLabel(item.fixRoute)} · {missingInputFocusLabel(item, project)}</small>
                  </span>
                  <button className="secondary-action" onClick={() => onResolveMissingInput(item)} type="button">
                    Fix source
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {localOnlySourceCount ? (
            <p className="package-muted-note package-local-note" role="status">
              <Icon name="upload" />
              {localOnlySourceCount} source{localOnlySourceCount === 1 ? "" : "s"} saved in this browser only — upload or retry in Assets to add {localOnlySourceCount === 1 ? "it" : "them"} to the cloud package. Nothing is lost.
            </p>
          ) : null}

          <div className={`package-readiness ${dryRunReady ? "ready" : "blocked"}`} role="status">
            <strong>{dryRunReady ? "Ready for dry-run test" : `Blocked · ${blockedReasons.length}`}</strong>
            <span>{dryRunReady ? "All required gates are checked. Live provider send is still disabled." : "Resolve missing items and gate checks before preparing the 480p test package."}</span>
          </div>

          <div className="rights-gate-card" aria-label="Rights and send confirmation">
            <div className="rights-gate-header">
              <span>Review Gate</span>
              <strong>{checkedGateCount} / {reviewGateItems.length}</strong>
            </div>
            {reviewGateItems.map((item) => (
              <label className={`rights-gate-row ${gateState[item.id] ? "checked" : ""}`} key={item.id}>
                <input
                  checked={gateState[item.id]}
                  onChange={() => toggleGate(item.id)}
                  type="checkbox"
                />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </span>
              </label>
            ))}
          </div>

          <button
            className="primary-action full"
            disabled={isSavingPackage || !dryRunReady}
            onClick={() => onSavePackage("send_test")}
            title={dryRunReady ? "Prepare a dry-run EvoLink test package" : blockedReasons.slice(0, 3).join(" · ")}
            type="button"
          >
            <Icon name="play" />
            {isSavingPackage ? "Building Package" : dryRunReady ? "Prepare 480p Test Package" : "Resolve Gates First"}
          </button>
          <small>V1 saves a dry-run package only. No provider spend occurs from this button.</small>

          <button className="secondary-action full" disabled={isSavingPackage} onClick={() => onSavePackage("save")} type="button">
            <Icon name="download" />
            Save package (dry-run JSON)
          </button>
        </aside>
      </div>
    </section>
  );
}
